import { useEffect, useState } from 'react';

// Australia Post's check of an Australian Suburb, State and Postcode as it's
// typed (#158). `fetchCheck(query)` asks the form's endpoint (staff,
// Account Application or portal): `{ postcode }` for Australia Post's
// Suburbs for a 4-digit Postcode, which the Suburb field offers and checks
// against, or `{ search }` for Suburbs whose names start with what's typed
// before a Postcode is in. Saving always goes ahead; the server checks
// again and keeps the result.

// The same comparison as backend/services/postcodeCheckService.js.
export function normaliseSuburb(value) {
  return String(value || '').toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').replace(/\bST\b/g, 'SAINT').replace(/\bMT\b/g, 'MOUNT').replace(/\s+/g, ' ').trim();
}

const isPostcode = (v) => /^\d{4}$/.test(String(v || '').trim());

// Australia Post's Suburbs for the Address's Postcode: an array, or null
// while there's no 4-digit Australian Postcode, or when Australia Post
// couldn't be asked (no key, unreachable), so nothing is offered or warned.
export function useAustraliaPostSuburbs(address, fetchCheck) {
  const [suburbs, setSuburbs] = useState(null);
  const country = address?.country;
  const postcode = String(address?.postcode || '').trim();
  useEffect(() => {
    setSuburbs(null);
    if (country !== 'Australia' || !isPostcode(postcode)) return undefined;
    let cancelled = false;
    fetchCheck({ postcode })
      .then((result) => {
        if (cancelled || !result) return;
        setSuburbs(result.message && !result.suburbs?.length ? null : (result.suburbs || []));
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // fetchCheck is the same call each render; only the Postcode matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, postcode]);
  return suburbs;
}

const SEARCH_MIN = 3;
const SEARCH_PAUSE_MS = 300;

// Australia Post's Suburbs whose names start with `text`, each with its
// State and Postcode, asked for after a short pause in typing once there
// are 3 letters: an array, or null while there's nothing to search.
export function useSuburbSearch(text, enabled, fetchCheck) {
  const [found, setFound] = useState(null);
  const q = String(text || '').trim();
  const searching = enabled && q.length >= SEARCH_MIN;
  useEffect(() => {
    if (!searching) {
      setFound(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchCheck({ search: q })
        .then((result) => { if (!cancelled) setFound(result?.suburbs || []); })
        .catch(() => {});
    }, SEARCH_PAUSE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // fetchCheck is the same call each render; only the text matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, searching]);
  return found;
}

// "A", "A and B", "A, B and C".
function listOf(names) {
  return names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const MAX_NAMED = 5;
const INCLUDING = 3;

// What's wrong with an Australian Suburb against Australia Post's Suburbs
// for its Postcode, naming what Australia Post does list, or undefined:
// none typed, a match, or nothing to check against.
export function suburbWarning(address, suburbs) {
  if (address?.country !== 'Australia') return undefined;
  const suburb = String(address.suburb || '').trim();
  const postcode = String(address.postcode || '').trim();
  if (!suburb || !postcode) return undefined;
  if (!isPostcode(postcode)) return 'An Australian Postcode is 4 digits.';
  if (!suburbs) return undefined;
  if (!suburbs.length) return `Australia Post has no Suburbs for Postcode ${postcode}.`;
  const state = String(address.state || '').trim().toUpperCase();
  const typed = normaliseSuburb(suburb);
  if (suburbs.some((s) => normaliseSuburb(s.suburb) === typed && (!state || s.state === state))) return undefined;

  const missing = `Australia Post doesn't list ${suburb} for ${[state, postcode].filter(Boolean).join(' ')}.`;
  // The right Suburb under another State: name it with its State.
  const sameName = suburbs.filter((s) => normaliseSuburb(s.suburb) === typed);
  if (sameName.length) return `${missing} It lists ${listOf(sameName.map((s) => `${s.suburb} ${s.state}`))}.`;
  const names = [...new Set(suburbs.map((s) => s.suburb))];
  if (names.length <= MAX_NAMED) return `${missing} It lists ${listOf(names)}.`;
  // Many: a few, those starting like what was typed first.
  const first = typed.charAt(0);
  const some = [...names.filter((n) => normaliseSuburb(n).startsWith(first)), ...names.filter((n) => !normaliseSuburb(n).startsWith(first))].slice(0, INCLUDING);
  return `${missing} It lists ${names.length} Suburbs, including ${listOf(some)}. Start typing to see them.`;
}

// The State that goes with a Suburb picked from Australia Post's list, when
// only one does.
export function stateForSuburb(value, suburbs) {
  const typed = normaliseSuburb(value);
  const states = [...new Set((suburbs || []).filter((s) => normaliseSuburb(s.suburb) === typed).map((s) => s.state))];
  return states.length === 1 ? states[0] : undefined;
}
